import React, { useState, useEffect, useCallback } from 'react';
import { useSecureAuth } from '../../contexts/SecureAuthContext';
import { useCurrency } from '../../contexts/CurrencyContext';
import { useToast } from '../../components/ui/Toast';
import { calculateMerchantTaxAmount, calculateMerchantTaxTotal } from '../../utils/merchantTax';
import './CreateOrderModal.css';

// Modern SVG Icons for sources
const SourceIcons = {
  PHONE_CALL: (
    <svg width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
      <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72 12.84 12.84 0 00.7 2.81 2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45 12.84 12.84 0 002.81.7A2 2 0 0122 16.92z" />
    </svg>
  ),
  SOCIAL_MEDIA_DM: (
    <svg width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
      <circle cx="12" cy="12" r="4" />
      <path d="M16 8v5a3 3 0 006 0v-1a10 10 0 10-3.92 7.94" />
    </svg>
  ),
  WALK_IN: (
    <svg width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
      <circle cx="13" cy="4" r="2" />
      <path d="M7 21l3-4v-6l-3-3 2-2 5 4v4l4 3" />
      <path d="M10 11l-2 7" />
    </svg>
  ),
  MARKET_STALL: (
    <svg width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
      <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z" />
      <polyline points="9 22 9 12 15 12 15 22" />
    </svg>
  ),
  EMAIL: (
    <svg width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
      <polyline points="22,6 12,13 2,6" />
    </svg>
  ),
  REFERRAL: (
    <svg width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
      <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75" />
    </svg>
  ),
  WHOLESALE: (
    <svg width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
      <path d="M21 16V8a2 2 0 00-1-1.73l-7-4a2 2 0 00-2 0l-7 4A2 2 0 003 8v8a2 2 0 001 1.73l7 4a2 2 0 002 0l7-4A2 2 0 0021 16z" />
      <polyline points="3.27 6.96 12 12.01 20.73 6.96" />
      <line x1="12" y1="22.08" x2="12" y2="12" />
    </svg>
  ),
  OTHER: (
    <svg width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
      <circle cx="5" cy="12" r="1.5" fill="currentColor" />
      <circle cx="12" cy="12" r="1.5" fill="currentColor" />
      <circle cx="19" cy="12" r="1.5" fill="currentColor" />
    </svg>
  )
};

const SALE_SOURCES = [
  { value: 'PHONE_CALL', label: 'Phone Call' },
  { value: 'SOCIAL_MEDIA_DM', label: 'Social Media DM' },
  { value: 'WALK_IN', label: 'Walk-in' },
  { value: 'MARKET_STALL', label: 'Market/Pop-up' },
  { value: 'EMAIL', label: 'Email' },
  { value: 'REFERRAL', label: 'Referral' },
  { value: 'WHOLESALE', label: 'Wholesale' },
  { value: 'OTHER', label: 'Other' }
];

const PAYMENT_METHODS = [
  { value: 'CASH', label: 'Cash' },
  { value: 'BANK_TRANSFER', label: 'Bank Transfer' },
  { value: 'POS_TERMINAL', label: 'POS Terminal' },
  { value: 'MOBILE_MONEY', label: 'Mobile Money' },
  { value: 'CHEQUE', label: 'Cheque' },
  { value: 'OTHER', label: 'Other' }
];

export default function CreateOrderModal({ isOpen, onClose, onSuccess }) {
  const { getAxios } = useSecureAuth();
  const { formatInDisplayCurrency, accountCurrency } = useCurrency();
  const toast = useToast();

  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [products, setProducts] = useState([]);
  const [taxSettings, setTaxSettings] = useState({ enabled: false, rate: 0, name: 'Tax', inclusive: false });
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showResults, setShowResults] = useState(false);

  // Form data
  const [formData, setFormData] = useState({
    customerName: '',
    customerEmail: '',
    customerPhone: '',
    shippingAddress: {
      street: '',
      city: '',
      state: '',
      country: 'Nigeria'
    },
    items: [],
    manualSaleSource: '',
    paymentStatus: 'PENDING',
    externalPaymentMethod: '',
    externalPaymentRef: '',
    notes: ''
  });

  // Fetch products for selection
  const fetchProducts = useCallback(async () => {
    try {
      const axios = getAxios();
      const response = await axios.get('/merchants/products', { params: { limit: 200 } });
      const productList = response.data.products || response.data || [];
      setProducts(Array.isArray(productList) ? productList : []);
    } catch (error) {
      console.error('Failed to fetch products:', error);
      setProducts([]);
    }
  }, [getAxios]);

  const fetchTaxSettings = useCallback(async () => {
    try {
      const axios = getAxios();
      const response = await axios.get('/merchant/payment-gateways/tax-settings');
      const next = response.data?.data || response.data?.taxSettings || response.data;
      if (next) {
        setTaxSettings({
          enabled: next.enabled === true,
          rate: Number(next.rate || 0),
          name: next.name || 'Tax',
          inclusive: next.inclusive === true
        });
      }
    } catch (error) {
      console.error('Failed to fetch merchant tax settings:', error);
    }
  }, [getAxios]);

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setStep(1);
      setFormData({
        customerName: '',
        customerEmail: '',
        customerPhone: '',
        shippingAddress: { street: '', city: '', state: '', country: 'Nigeria' },
        items: [],
        manualSaleSource: '',
        paymentStatus: 'PENDING',
        externalPaymentMethod: '',
        externalPaymentRef: '',
        notes: ''
      });
      setSearchQuery('');
      setSearchResults([]);
      fetchProducts();
      fetchTaxSettings();
    }
  }, [isOpen, fetchProducts, fetchTaxSettings]);

  // Search products
  useEffect(() => {
    if (searchQuery.trim() && products.length > 0) {
      const query = searchQuery.toLowerCase();
      const filtered = products.filter(p =>
        p.name?.toLowerCase().includes(query) ||
        p.sku?.toLowerCase().includes(query)
      );
      setSearchResults(filtered.slice(0, 10));
      setShowResults(true);
    } else {
      setSearchResults([]);
      setShowResults(false);
    }
  }, [searchQuery, products]);

  // Calculate totals
  const subtotal = formData.items.reduce((sum, item) => sum + (item.price * item.qty), 0);
  const taxRate = taxSettings.enabled ? Number(taxSettings.rate || 0) : 0;
  const taxTotal = calculateMerchantTaxAmount(subtotal, taxSettings);
  const total = calculateMerchantTaxTotal(subtotal, taxSettings);

  // Add item to order
  const addItem = (product) => {
    const existingIndex = formData.items.findIndex(i => i.productId === product.id);
    if (existingIndex >= 0) {
      const newItems = [...formData.items];
      newItems[existingIndex].qty += 1;
      setFormData({ ...formData, items: newItems });
    } else {
      setFormData({
        ...formData,
        items: [...formData.items, {
          productId: product.id,
          name: product.name,
          price: parseFloat(product.price) || 0,
          qty: 1
        }]
      });
    }
    setSearchQuery('');
    setSearchResults([]);
    setShowResults(false);
  };

  // Add custom item
  const addCustomItem = () => {
    setFormData({
      ...formData,
      items: [...formData.items, {
        productId: null,
        name: '',
        price: 0,
        qty: 1,
        isCustom: true
      }]
    });
  };

  // Update item
  const updateItem = (index, field, value) => {
    const newItems = [...formData.items];
    newItems[index][field] = field === 'price' || field === 'qty' ? parseFloat(value) || 0 : value;
    setFormData({ ...formData, items: newItems });
  };

  // Remove item
  const removeItem = (index) => {
    const newItems = formData.items.filter((_, i) => i !== index);
    setFormData({ ...formData, items: newItems });
  };

  // Submit order
  const handleSubmit = async () => {
    if (!formData.customerName) {
      toast.error('Customer name is required');
      return;
    }
    if (!formData.manualSaleSource) {
      toast.error('Please select order source');
      return;
    }
    if (formData.items.length === 0) {
      toast.error('Please add at least one item');
      return;
    }

    setLoading(true);
    try {
      const axios = getAxios();
      const response = await axios.post('/merchant/manual-orders/create', {
        ...formData,
        subtotal,
        taxTotal,
        total,
        shippingFee: 0,
        discount: 0
      });

      if (response.data.success) {
        toast.success('Order created successfully!');
        onSuccess && onSuccess(response.data.order);
        onClose();
      } else {
        toast.error(response.data.error || 'Failed to create order');
      }
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to create order');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="co-modal-overlay" onClick={onClose}>
      <div className="co-modal" onClick={e => e.stopPropagation()}>
        <div className="co-modal-header">
          <h2>Create Order</h2>
          <p className="co-modal-subtitle">Create a manual order for fulfillment</p>
          <button className="co-close-btn" onClick={onClose}>
            <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Progress Steps */}
        <div className="co-steps">
          <div className={`co-step ${step >= 1 ? 'active' : ''}`}>
            <span className="co-step-num">1</span>
            <span className="co-step-label">Source</span>
          </div>
          <div className={`co-step ${step >= 2 ? 'active' : ''}`}>
            <span className="co-step-num">2</span>
            <span className="co-step-label">Customer</span>
          </div>
          <div className={`co-step ${step >= 3 ? 'active' : ''}`}>
            <span className="co-step-num">3</span>
            <span className="co-step-label">Items</span>
          </div>
          <div className={`co-step ${step >= 4 ? 'active' : ''}`}>
            <span className="co-step-num">4</span>
            <span className="co-step-label">Review</span>
          </div>
        </div>

        <div className="co-modal-body">
          {/* Step 1: Order Source */}
          {step === 1 && (
            <div className="co-step-content">
              <h3>Where did this order come from?</h3>
              <div className="co-source-grid">
                {SALE_SOURCES.map(source => (
                  <button
                    key={source.value}
                    className={`co-source-btn ${formData.manualSaleSource === source.value ? 'selected' : ''}`}
                    onClick={() => setFormData({ ...formData, manualSaleSource: source.value })}
                  >
                    <span className="co-source-icon">{SourceIcons[source.value]}</span>
                    <span className="co-source-label">{source.label}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Step 2: Customer Info */}
          {step === 2 && (
            <div className="co-step-content">
              <h3>Customer Information</h3>
              <div className="co-form-group">
                <label>Customer Name *</label>
                <input
                  type="text"
                  value={formData.customerName}
                  onChange={e => setFormData({ ...formData, customerName: e.target.value })}
                  placeholder="Enter customer name"
                />
              </div>
              <div className="co-form-row">
                <div className="co-form-group">
                  <label>Email</label>
                  <input
                    type="email"
                    value={formData.customerEmail}
                    onChange={e => setFormData({ ...formData, customerEmail: e.target.value })}
                    placeholder="customer@email.com"
                  />
                </div>
                <div className="co-form-group">
                  <label>Phone</label>
                  <input
                    type="tel"
                    value={formData.customerPhone}
                    onChange={e => setFormData({ ...formData, customerPhone: e.target.value })}
                    placeholder="+234..."
                  />
                </div>
              </div>
              <h4>Shipping Address</h4>
              <div className="co-form-group">
                <label>Street Address</label>
                <input
                  type="text"
                  value={formData.shippingAddress.street}
                  onChange={e => setFormData({
                    ...formData,
                    shippingAddress: { ...formData.shippingAddress, street: e.target.value }
                  })}
                  placeholder="Street address"
                />
              </div>
              <div className="co-form-row">
                <div className="co-form-group">
                  <label>City</label>
                  <input
                    type="text"
                    value={formData.shippingAddress.city}
                    onChange={e => setFormData({
                      ...formData,
                      shippingAddress: { ...formData.shippingAddress, city: e.target.value }
                    })}
                    placeholder="City"
                  />
                </div>
                <div className="co-form-group">
                  <label>State</label>
                  <input
                    type="text"
                    value={formData.shippingAddress.state}
                    onChange={e => setFormData({
                      ...formData,
                      shippingAddress: { ...formData.shippingAddress, state: e.target.value }
                    })}
                    placeholder="State"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Step 3: Items */}
          {step === 3 && (
            <div className="co-step-content">
              <h3>Order Items</h3>
              <div className="co-product-search">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  onFocus={() => searchQuery && setShowResults(true)}
                  placeholder="Search products by name or SKU..."
                />
                {showResults && searchResults.length > 0 && (
                  <div className="co-search-results">
                    {searchResults.map(product => (
                      <button
                        key={product.id}
                        className="co-search-item"
                        onClick={() => addItem(product)}
                      >
                        <span>{product.name}</span>
                        <span>{formatInDisplayCurrency(product.price, accountCurrency)}</span>
                      </button>
                    ))}
                  </div>
                )}
                {showResults && searchQuery && searchResults.length === 0 && (
                  <div className="co-search-results">
                    <div className="co-search-empty">No products found matching "{searchQuery}"</div>
                  </div>
                )}
              </div>
              <button className="co-add-custom-btn" onClick={addCustomItem}>
                <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <line x1="12" y1="5" x2="12" y2="19" />
                  <line x1="5" y1="12" x2="19" y2="12" />
                </svg>
                Add Custom Item
              </button>

              <div className="co-items-list">
                {formData.items.length === 0 ? (
                  <p className="co-no-items">No items added yet. Search for products above or add a custom item.</p>
                ) : (
                  formData.items.map((item, index) => (
                    <div key={index} className="co-item-row">
                      {item.isCustom ? (
                        <input
                          type="text"
                          className="co-item-name-input"
                          value={item.name}
                          onChange={e => updateItem(index, 'name', e.target.value)}
                          placeholder="Item name"
                        />
                      ) : (
                        <span className="co-item-name">{item.name}</span>
                      )}
                      <input
                        type="number"
                        className="co-item-qty"
                        value={item.qty}
                        onChange={e => updateItem(index, 'qty', e.target.value)}
                        min="1"
                      />
                      <input
                        type="number"
                        className="co-item-price"
                        value={item.price}
                        onChange={e => updateItem(index, 'price', e.target.value)}
                        step="0.01"
                      />
                      <span className="co-item-total">
                        {formatInDisplayCurrency(item.price * item.qty, accountCurrency)}
                      </span>
                      <button className="co-remove-btn" onClick={() => removeItem(index)}>
                        <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                          <path d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  ))
                )}
              </div>

              <div className="co-totals">
                <div className="co-total-row">
                  <span>Subtotal</span>
                  <span>{formatInDisplayCurrency(subtotal, accountCurrency)}</span>
                </div>
                {taxTotal > 0 && (
                  <div className="co-total-row">
                    <span>{taxSettings.name || 'Tax'} ({taxRate}%{taxSettings.inclusive ? ' incl.' : ''})</span>
                    <span>{formatInDisplayCurrency(taxTotal, accountCurrency)}</span>
                  </div>
                )}
                <div className="co-total-row co-grand-total">
                  <span>Total</span>
                  <span>{formatInDisplayCurrency(total, accountCurrency)}</span>
                </div>
              </div>
            </div>
          )}

          {/* Step 4: Review & Payment */}
          {step === 4 && (
            <div className="co-step-content">
              <h3>Review & Payment Status</h3>

              <div className="co-review-section">
                <h4>Order Summary</h4>
                <p><strong>Source:</strong> {SALE_SOURCES.find(s => s.value === formData.manualSaleSource)?.label}</p>
                <p><strong>Customer:</strong> {formData.customerName}</p>
                <p><strong>Items:</strong> {formData.items.length}</p>
                <p><strong>Total:</strong> {formatInDisplayCurrency(total, accountCurrency)}</p>
              </div>

              <div className="co-form-group">
                <label>Payment Status</label>
                <select
                  value={formData.paymentStatus}
                  onChange={e => setFormData({ ...formData, paymentStatus: e.target.value })}
                >
                  <option value="PENDING">Unpaid (Pay on Delivery)</option>
                  <option value="COMPLETED">Already Paid</option>
                </select>
              </div>

              {formData.paymentStatus === 'COMPLETED' && (
                <>
                  <div className="co-form-group">
                    <label>Payment Method</label>
                    <select
                      value={formData.externalPaymentMethod}
                      onChange={e => setFormData({ ...formData, externalPaymentMethod: e.target.value })}
                    >
                      <option value="">Select payment method</option>
                      {PAYMENT_METHODS.map(m => (
                        <option key={m.value} value={m.value}>{m.label}</option>
                      ))}
                    </select>
                  </div>
                  <div className="co-form-group">
                    <label>Payment Reference</label>
                    <input
                      type="text"
                      value={formData.externalPaymentRef}
                      onChange={e => setFormData({ ...formData, externalPaymentRef: e.target.value })}
                      placeholder="Transaction reference (optional)"
                    />
                  </div>
                </>
              )}

              <div className="co-form-group">
                <label>Notes</label>
                <textarea
                  value={formData.notes}
                  onChange={e => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="Additional notes..."
                  rows={3}
                />
              </div>
            </div>
          )}
        </div>

        <div className="co-modal-footer">
          {step > 1 && (
            <button className="co-btn co-btn-secondary" onClick={() => setStep(step - 1)}>
              Back
            </button>
          )}
          <div className="co-footer-spacer" />
          {step < 4 ? (
            <button
              className="co-btn co-btn-primary"
              onClick={() => {
                if (step === 1 && !formData.manualSaleSource) {
                  toast.warning('Please select an order source');
                  return;
                }
                if (step === 2 && !formData.customerName) {
                  toast.warning('Customer name is required');
                  return;
                }
                if (step === 3 && formData.items.length === 0) {
                  toast.warning('Please add at least one item');
                  return;
                }
                setStep(step + 1);
              }}
            >
              Continue
            </button>
          ) : (
            <button
              className="co-btn co-btn-primary"
              onClick={handleSubmit}
              disabled={loading}
            >
              {loading ? 'Creating...' : 'Create Order'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
